import { camelCase, range } from 'lodash-es';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Select from '../../../../../../../atoms/Select';
import LabeledContainer from '../../../../../../../molecules/common/LabeledContainer';
import DeletableTextInput from '../../../../../../../molecules/common/DeletableTextInput';
import FormErrorMessage from '../../../../../../../atoms/FormErrorMessage';
import { TextInputType } from '../../../../../../../atoms/TextInput';
import SecurityCodeMessage from '../../../../../../../molecules/common/SecurityCodeMessage';
import * as IBooks from '../../../../../../../interfaces/merchants/resources/books';
import * as $ from '../../../../../../../helpers/merchants/resources/helper';
import { CreditCard } from '../../../../../../../interfaces/creditCard';

interface Props {
  paymentMethod?: IBooks.PaymentMethod;
  creditCard: CreditCard;
  onChange: (creditCard: CreditCard) => void;
  error?: IBooks.CreditCardError;
}

export const submittable = (creditCard: CreditCard): boolean =>
  !!creditCard.cardNumber &&
  !!creditCard.securityCode &&
  !!creditCard.month &&
  !!creditCard.year;

const InputPaymentCreditCard: React.FC<Props> = (props) => {
  const { error, creditCard, paymentMethod, onChange } = props;
  const intl = useIntl();

  const createYearOptions = () => {
    const thisYear = new Date().getFullYear();
    return range(thisYear, thisYear + 15).map((year) => ({
      label: year.toString(),
      value: year,
    }));
  };

  const createMonthOptions = () => {
    return range(1, 12 + 1).map((month) => ({
      label: month.toString(),
      value: month,
    }));
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const {
        currentTarget: { name, value },
      } = e;
      const changedState = $.changeObjectByNestedString(
        { creditCard: { ...creditCard } },
        name,
        value,
      );
      onChange((changedState as Props).creditCard);
    },
    [creditCard, onChange],
  );

  if (!paymentMethod || paymentMethod.method !== 'credit_card') {
    return null;
  }

  return (
    <div>
      {error && error.field === 'card' && (
        <div>
          {intl.formatMessage({
            id: `books.errorMessage.${camelCase(error.type)}`,
          })}
        </div>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.creditCardNumber',
        })}
        paddingTop={true}
        requiredLabel={true}
        errorStyle={error && error.field === 'number'}
      >
        <DeletableTextInput
          type={TextInputType.Text}
          value={creditCard.cardNumber || ''}
          name="creditCard.cardNumber"
          // @ts-expect-error strictFunctionTypes
          onChange={handleChange}
        />
      </LabeledContainer>
      {error && error.field === 'number' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `books.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.expirationYear',
        })}
        requiredLabel={true}
        errorStyle={error && error.field === 'year'}
      >
        <Select
          name="creditCard.year"
          value={creditCard.year || ''}
          onChange={handleChange}
          selected={creditCard.year !== undefined}
        >
          {createYearOptions().map((o, i) => (
            <FormattedMessage
              id="books.formsLabel.year"
              values={{ year: o.label }}
              key={i}
            >
              {(year) => <option value={o.value}>{year}</option>}
            </FormattedMessage>
          ))}
        </Select>
      </LabeledContainer>
      {error && error.field === 'year' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `books.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.expirationMonth',
        })}
        requiredLabel={true}
        errorStyle={error && error.field === 'month'}
      >
        <Select
          name="creditCard.month"
          value={creditCard.month || ''}
          onChange={handleChange}
          selected={creditCard.month !== undefined}
        >
          {createMonthOptions().map((o, i) => (
            <FormattedMessage
              id="books.formsLabel.month"
              values={{ month: o.label }}
              key={i}
            >
              {(month) => <option value={o.value}>{month}</option>}
            </FormattedMessage>
          ))}
        </Select>
      </LabeledContainer>
      {error && error.field === 'month' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `books.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.securityCode',
        })}
        requiredLabel={true}
        errorStyle={error && error.field === 'cvc'}
      >
        <DeletableTextInput
          type={TextInputType.Text}
          value={creditCard.securityCode || ''}
          name="creditCard.securityCode"
          // @ts-expect-error strictFunctionTypes
          onChange={handleChange}
        />
      </LabeledContainer>
      {error && error.field === 'cvc' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `books.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <SecurityCodeMessage />
    </div>
  );
};

export default InputPaymentCreditCard;
